<template>
  <div>
    <!-- Site information -->
    <v-container fluid class="pb-6">
      <gg-card>
        <gg-card-header>
          <v-row>
            <v-col cols="12" class="d-flex justify-lg-space-between">
              <div class="d-inline-flex">
                Site Information
                <v-icon v-if="info_collapsed" color="primary" class="ml-3" >
                  mdi-checkbox-marked-circle
                </v-icon>
              </div>
              <div v-if="info_collapsed">
                <gg-btn icon x-small @click="info_collapsed = false">
                  <v-icon color="primary">
                    mdi-pencil
                  </v-icon>
                </gg-btn>
              </div>
            </v-col>
          </v-row>
        </gg-card-header>
        <gg-card-text>
          <v-row>
            <v-col cols="12">
              <span v-if="info_collapsed">Your website will be installed on {{ websiteName }}</span>
              <span v-else>
                If you change your mind, you may <a @click="setStep(1)">
                  go back
                </a> and select a different option.
              </span>
            </v-col>
          </v-row>
          <v-row v-if="!info_collapsed">
            <v-col cols="12">
              <v-form>
                <gg-text-field v-model="siteName"
                               label="Site / Project name"
                               name="project-name"
                               outlined
                               autocomplete="new-password"
                               v-validate="'required'"
                               data-vv-as="Site / Project name"
                               :error-messages="errors.collect('project-name')"
                />
                <gg-text-field v-model="siteDescription"
                              label="Site Description"
                              name="site-description"
                              outlined
                              autocomplete="new-password"
                              data-vv-as="Site Description"
                              :error-messages="errors.collect('site-description')"
                />
                <v-row>
                  <v-col cols="12" md="6">
                    <gg-text-field v-model="protocol"
                                  label="Protocol"
                                  name="protocol"
                                  disabled
                                  outlined
                                  autocomplete="new-password"
                                  v-validate="'required'"
                                  data-vv-as="Protocol"
                                  :error-messages="errors.collect('protocol')"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <gg-text-field v-if="!isModeManual" v-model="domain"
                                  :value="service"
                                  label="Domain"
                                  name="domain"
                                  disabled
                                  outlined
                                  autocomplete="new-password"
                                  v-validate="'required'"
                                  data-vv-as="Domain"
                                  :error-messages="errors.collect('domain')"
                    />
                    <gg-select v-else
                              v-model="domain"
                              :items="second_step.domains"
                              label="Domain"
                              name="domain"
                              outlined
                              autocomplete="new-password"
                              v-validate="'required'"
                              data-vv-as="Domain"
                              :error-messages="errors.collect('domain')"
                              @input="toggleDomain"
                    />
                  </v-col>
                </v-row>
              </v-form>
            </v-col>
          </v-row>
        </gg-card-text>
      </gg-card>
    </v-container>

    <!-- SSL -->
    <v-container fluid class="py-6">
      <gg-card>
        <gg-card-header>
          <v-row>
            <v-col cols="12" class="d-flex justify-lg-space-between">
              <div class="d-inline-flex">
                SSL Configuration
                <v-icon v-if="ssl || domainHasSSL" color="primary" class="ml-3" small>
                  mdi-checkbox-marked-circle
                </v-icon>
              </div>
              <div v-if="ssl_collapsed">
                <gg-btn icon x-small @click="ssl_collapsed = false">
                  <v-icon color="primary">
                    mdi-pencil
                  </v-icon>
                </gg-btn>
              </div>
            </v-col>
          </v-row>
        </gg-card-header>
        <gg-card-text>
          <v-row v-if="ssl_collapsed">
            <v-col cols="12" v-if="domainHasSSL">
              Your website {{ websiteName }} will be secured with an SSL certificate.
            </v-col>
          </v-row>
          <v-row v-else>
            <v-col v-if="domainHasSSL" cols="12" class="py-0">
              <p class="primary--text body-1 mb-0">
                <v-icon size="20" class="mb-1" color="primary">mdi-check-circle</v-icon>
                Excellent, SSL is already enabled
              </p>
            </v-col>
            <v-col v-else cols="12" class="py-0">
              <v-switch v-model="ssl" label="Enable SSL" @change="toggleSslSwitch" class="mt-0">
                Enable SSL
              </v-switch>
              <p class="mb-0" v-if="ssl">
                A Let’s Encrypt SSL certificate will be deployed with your website.
              </p>
            </v-col>
          </v-row>
        </gg-card-text>
      </gg-card>
    </v-container>
    <!-- Themes -->
    <v-container v-show="second_step.themes.length" fluid class="py-6">
      <gg-card>
        <gg-card-header>
          Pick a Theme
        </gg-card-header>
        <gg-card-text>
          <v-row>
            <v-col cols="12">
              Select a theme to use for your website. If you do not select a theme, the default WordPress theme will
              be used. You can always change this later.
            </v-col>
            <v-col cols="12">
              <data-view cols="3"
                         :items="second_step.themes"
                         :initial-value="theme"
                         v-model="theme"
                         :mandatory="true"
                         theme
              />
            </v-col>
          </v-row>
        </gg-card-text>
      </gg-card>
    </v-container>
    <!-- Plugins -->
    <v-container v-show="second_step.plugins.length" fluid class="py-6">
      <gg-card>
        <gg-card-header>
          Select Plugins
        </gg-card-header>
        <gg-card-text>
          <v-row>
            <v-col cols="12">
              Plugins add additional capability to your website. You can install plugins later if you aren’t sure yet.
            </v-col>
            <v-col cols="12">
              <data-view cols="3"
                         :items="second_step.plugins"
                         :initial-value="plugins"
                         v-model="plugins"
                         plugins-dialog
                         multiple
                         :svg="false"
              />
            </v-col>
          </v-row>
        </gg-card-text>
      </gg-card>
    </v-container>
    <!-- Caching -->
    <v-container fluid class="py-6">
      <gg-card>
        <gg-card-header>
          Powered by LiteSpeed & LSCache
          <v-icon color="primary" class="ml-3" small>mdi-checkbox-marked-circle</v-icon>
        </gg-card-header>
        <gg-card-text>
          <v-row>
            <v-col cols="12" class="pb-0">
            </v-col>
            <v-col cols="12" md="8">
              <p class="mb-2">
                GreenGeeks websites run on LiteSpeed servers, so you can take full advantage of the LiteSpeed Cache
                website acceleration plugin. With advanced cache-management tools and optimization features. LSCache
                for WordPress is an all-in-one site acceleration solution. It's the only optimization plugin you need!
              </p>
              <p>
                Caching via LiteSpeed is activated by default so your site can employ the best practices that make
                for a high PageSpeed score, and more importantly an incredible user experience . You can disable
                this at any time from within your WP Dashboard.
              </p>
              <v-row>
                <v-col cols="12" md="4">
                  <v-icon medium color="primary">mdi-clipboard-arrow-down-outline</v-icon>
                  Server-Level Full-Page Cache
                </v-col>
                <v-col cols="12" md="4">
                  <v-icon medium color="primary">mdi-lock</v-icon>
                  Private Cache
                </v-col>
                <v-col cols="12" md="4">
                  <v-icon medium color="primary">mdi-checkbox-blank-circle-outline</v-icon>
                  Edge Side Includes (ESI)
                </v-col>
                <v-col cols="12" md="4">
                  <v-icon medium color="primary">mdi-image-size-select-actual</v-icon>
                  Image Optimization
                </v-col>
                <v-col cols="12" md="4">
                  <v-icon medium color="primary">mdi-cogs</v-icon>
                  Crawler
                </v-col>
                <v-col cols="12" md="4">
                  <v-icon medium color="primary">mdi-arrow-collapse-vertical</v-icon>
                  CSS/JavaScript/HTML Minify
                </v-col>
                <v-col cols="12" md="4">
                  <v-icon medium color="primary">mdi-folder-outline</v-icon>
                  CSS/JavaScript Combination
                </v-col>
                <v-col cols="12" md="4">
                  <v-icon medium color="primary">mdi-telegram</v-icon>
                  HTTP/2 Push
                </v-col>
                <v-col cols="12" md="4">
                  <v-icon medium color="primary">mdi-cloud-download</v-icon>
                  Content Delivery Network Support
                </v-col>
                <v-col cols="12" md="4">
                  <v-icon medium color="primary">mdi-file-code-outline</v-icon>
                  Browser Cache Support
                </v-col>
                <v-col cols="12" md="4">
                  <v-icon medium color="primary">mdi-image-frame</v-icon>
                  Lazy Load Images
                </v-col>
                <v-col cols="12" md="4">
                  <v-icon medium color="primary">mdi-database</v-icon>
                  Database Optimization
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" md="4" class="ma-auto">
              <v-img :src="`/static/img/wizard/litespeed-logo.png`"
                     height="280" width="280"
                     class="d-flex align-center ma-auto"/>
            </v-col>
          </v-row>
        </gg-card-text>
      </gg-card>
    </v-container>
    <!-- Submit, errors -->
    <v-container>
      <v-row>
        <gg-error :data="this.errors.all()" />
        <v-col cols="12" class="text-center">
          <gg-btn secondary @click="setStep(1)" tile>
            Back
          </gg-btn>
          <gg-btn color="primary" tile class="mx-1" :loading="loading" @click="submit">
            Create My website
          </gg-btn>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { cardActiveStyle } from '@/helpers/wizard'
import DataView from '@/components/Wizard/DataView'
import amapi from '@/api/amapi'

export default {
  name: 'WordPress',
  components: {
    DataView,
  },
  props: {
    loading: {
      type: Boolean
    },
    loadingApps: {
      type: Boolean
    },
    appsError: {
      type: String
    },
  },
  data: () => ({
    protocol: 'http://www.',
    info_collapsed: true,
    ssl_collapsed: false,
    // login_collapsed: false
  }),
  computed: {
    ...mapGetters({
      // profile: 'SessionProfile',
      ssls: 'ssls',
      items: 'wizard/second_step_data',
      second_step: 'wizard/second_step',
      selected: 'wizard_model/wordpress',
      service_id: 'wizard/service_id',
      service: 'wizard/serviceDomain',
      mode: 'wizard/mode'
    }),
    domain: {
      get () {
        return this.selected.domain
      },
      set (val) {
        this.setDomain(val)
      }
    },
    siteName: {
      get () {
        return this.selected.siteName
      },
      set (val) {
        this.setSitename(val)
      }
    },
    ssl: {
      get () {
        return this.selected.ssl
      },
      set (val) {
        this.setSsl(val)
      }
    },
    // email: {
    //   get () {
    //     return this.selected.email
    //   },
    //   set (val) {
    //     this.setEmail(val)
    //   }
    // },
    // password: {
    //   get () {
    //     return this.selected.password
    //   },
    //   set (val) {
    //     this.setPassword(val)
    //   }
    // },
    // password_confirm: {
    //   get () {
    //     return this.selected.password_confirm
    //   },
    //   set (val) {
    //     this.setPasswordConfirm(val)
    //   }
    // },
    siteDescription: {
      get () {
        return this.selected.siteDescription
      },
      set (val) {
        this.setSiteDescription(val)
      }
    },
    theme: {
      get () {
        return this.selected.theme
      },
      set (val) {
        this.setTheme(val)
      }
    },
    plugins: {
      get () {
        return this.selected.plugins
      },
      set (val) {
        this.setPlugins(val)
      }
    },
    caching: {
      get () {
        return this.selected.caching
      },
      set (val) {
        this.setCaching(val)
      }
    },
    domainHasSSL () {
      if (!this.domain) return false
      const ssl = this.ssls.find(ssl => {
        const subdomains = ssl.domain.replace('*', '')
        const domain = ssl.domain.replace('*.', '')
        return this.domain == domain || this.domain.endsWith(subdomains)
      })
      return !!ssl
    },
    isModeManual () {
      return this.mode === 'manual'
    },
    websiteName () {
      return this.protocol + this.domain
    }
  },
  mounted () {
    // this.email = this.profile.email
    this.checkSSL()
    this.$nextTick(() => {
      this.siteName = 'WordPress'
      this.siteDescription = 'My new WordPress website'
      this.domain = this.second_step.domains[0]
      this.setExistsSSL(this.domainHasSSL)
      if (this.domainHasSSL) {
        this.ssl_collapsed = true
      }
      this.setInitialProtocolState()
    })
  },
  methods: {
    cardActiveStyle,
    ...mapMutations({
      setStep: 'wizard/SET_STEP',
      setDomain: 'wizard_model/SET_WORDPRESS_DOMAIN',
      setSitename: 'wizard_model/SET_WORDPRESS_SITENAME',
      setSsl: 'wizard_model/SET_WORDPRESS_SSL',
      // setEmail: 'wizard_model/SET_WORDPRESS_EMAIL',
      // setPassword: 'wizard_model/SET_WORDPRESS_PASSWORD',
      // setPasswordConfirm: 'wizard_model/SET_WORDPRESS_PASSWORD_CONFIRM',
      setSiteDescription: 'wizard_model/SET_WORDPRESS_DESCRIPTION',
      setTheme: 'wizard_model/SET_WORDPRESS_THEME',
      setPlugins: 'wizard_model/SET_WORDPRESS_PLUGINS',
      setCaching: 'wizard_model/SET_WORDPRESS_CACHING',
      setExistsSSL: 'wizard_model/SET_WORDPRESS_SSL_EXISTS',
    }),
    ...mapActions({
      reportError: 'reportError'
    }),
    setInitialProtocolState () {
      if (this.domainHasSSL || this.ssl) {
        this.protocol = 'https://www.'
      } else {
        this.protocol = 'http://www.'
      }
    },
    toggleSslSwitch () {
      this.protocol = this.ssl ? 'https://www.' : 'http://www.'
    },
    toggleDomain () {
      this.ssl = this.domainHasSSL
      console.log(this.domainHasSSL)
      this.setExistsSSL(this.domainHasSSL)
    },
    async checkSSL () {
      const payload = {
        id: this.service_id,
        params: {
          uapi_module: 'SSL',
          uapi_function: 'installed_hosts',
        }
      }

      const resp = await amapi.makecall('cpanel_uapi', payload)

      if (resp.payload.data.length !== 0) {
        this.ssl = true
        this.setExistsSSL(true)
      }
    },
    // async fillPassword () {
    //   const password = await this.$validator.validate('password')
    //   const password_confirmation = await this.$validator.validate('password_confirmation')
    //   if (password && password_confirmation) {
    //     this.login_collapsed = true
    //   }
    // },
    async submit () {
      try {
        if (!await this.$validator.validateAll()) {
          return
        }
        this.$emit('submit')
      } catch (e) {
        this.reportError(e)
      }
    }
  }
}
</script>

<style lang="scss">
  .disabled-item {
    pointer-events: none
  }
</style>
